import { db } from '../config/firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';

/**
 * BPI (Barber Performance Index) Factors
 * All scores are on a scale of 0-100 unless otherwise noted
 */
export interface BPIFactors {
  // Core Performance Metrics
  revenuePerformance: number;          // 0-100: Revenue vs goals
  clientRetention: number;             // 0-100: Client retention rate
  averageTicket: number;               // 0-100: Avg ticket vs target
  productSalesRate: number;            // 0-100: Product upsell rate

  // Service Quality
  clientSatisfaction: number;          // 0-100: Average rating
  serviceSpeed: number;                // 0-100: Efficiency score
  rebookRate: number;                  // 0-100: Rebook percentage
  noShowRate: number;                  // 0-100: Lower is better (inverted in calc)

  // Professional Standards
  punctuality: number;                 // 0-100: On-time percentage
  uniformCompliance: number;           // 0-100: Dress code adherence
  workstationCleanliness: number;      // 0-100: Cleanliness score

  // Growth & Development
  skillDevelopment: number;            // 0-100: Training/certification progress
  teamwork: number;                    // 0-100: Team collaboration score
  initiative: number;                  // 0-100: Proactive behavior

  // Client Relations
  newClientAcquisition: number;        // 0-100: New clients vs goal
  clientCommunication: number;         // 0-100: Communication quality
  socialMediaEngagement: number;       // 0-100: Social media activity
}

export interface BPIData {
  id?: string;
  barberId: string;
  barberName: string;
  factors: BPIFactors;

  // Calculated BPI Score (0-30 scale)
  bpiScore: number;

  // Metadata
  lastUpdated: Date;
  updatedBy: string;
  notes: string;

  // Manual adjustments (for special circumstances)
  manualAdjustment: number;            // -5 to +5 points
  adjustmentReason: string;
}

/**
 * Calculate BPI score from factors
 * Returns a score on a 0-30 scale
 */
export const calculateBPIScore = (factors: BPIFactors, manualAdjustment: number = 0): number => {
  // Weight categories
  const weights = {
    corePerformance: 0.35,      // 35%
    serviceQuality: 0.30,        // 30%
    professionalStandards: 0.15, // 15%
    growthDevelopment: 0.10,     // 10%
    clientRelations: 0.10        // 10%
  };

  // Calculate category scores (0-100)
  const corePerformance = (
    factors.revenuePerformance +
    factors.clientRetention +
    factors.averageTicket +
    factors.productSalesRate
  ) / 4;

  const serviceQuality = (
    factors.clientSatisfaction +
    factors.serviceSpeed +
    factors.rebookRate +
    (100 - factors.noShowRate) // Invert no-show rate
  ) / 4;

  const professionalStandards = (
    factors.punctuality +
    factors.uniformCompliance +
    factors.workstationCleanliness
  ) / 3;

  const growthDevelopment = (
    factors.skillDevelopment +
    factors.teamwork +
    factors.initiative
  ) / 3;

  const clientRelations = (
    factors.newClientAcquisition +
    factors.clientCommunication +
    factors.socialMediaEngagement
  ) / 3;

  // Weighted total (0-100)
  const weightedScore = (
    corePerformance * weights.corePerformance +
    serviceQuality * weights.serviceQuality +
    professionalStandards * weights.professionalStandards +
    growthDevelopment * weights.growthDevelopment +
    clientRelations * weights.clientRelations
  );

  // Convert to 0-30 scale
  let bpiScore = (weightedScore / 100) * 30;

  // Apply manual adjustment
  bpiScore += manualAdjustment;

  // Ensure score is within bounds
  return Math.max(0, Math.min(30, bpiScore));
};

/**
 * Get BPI data for a barber
 */
export const getBPIData = async (barberId: string): Promise<BPIData | null> => {
  try {
    const docRef = doc(db, 'bpiData', barberId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    } as BPIData;
  } catch (error) {
    console.error('Error fetching BPI data:', error);
    return null;
  }
};

/**
 * Save or update BPI data
 */
export const saveBPIData = async (
  bpiData: Omit<BPIData, 'id' | 'lastUpdated' | 'bpiScore'>,
  updatedBy: string
): Promise<void> => {
  try {
    const docRef = doc(db, 'bpiData', bpiData.barberId);

    // Calculate BPI score
    const bpiScore = calculateBPIScore(bpiData.factors, bpiData.manualAdjustment);

    const dataToSave = {
      ...bpiData,
      bpiScore,
      lastUpdated: Timestamp.now(),
      updatedBy
    };

    await setDoc(docRef, dataToSave, { merge: true });
  } catch (error) {
    console.error('Error saving BPI data:', error);
    throw error;
  }
};

/**
 * Update specific BPI factors
 */
export const updateBPIFactors = async (
  barberId: string,
  factors: Partial<BPIFactors>,
  updatedBy: string
): Promise<void> => {
  try {
    const docRef = doc(db, 'bpiData', barberId);
    const currentData = await getBPIData(barberId);

    if (!currentData) {
      throw new Error('BPI data not found');
    }

    const updatedFactors = { ...currentData.factors, ...factors };
    const bpiScore = calculateBPIScore(updatedFactors, currentData.manualAdjustment);

    await updateDoc(docRef, {
      factors: updatedFactors,
      bpiScore,
      lastUpdated: Timestamp.now(),
      updatedBy
    });
  } catch (error) {
    console.error('Error updating BPI factors:', error);
    throw error;
  }
};

/**
 * Generate default BPI data from barber performance
 */
export const generateDefaultBPIData = (
  barberId: string,
  barberName: string,
  performanceData: {
    revenuePerformance: number;
    retentionRate: number;
    avgTicket: number;
    clientSatisfaction: number;
  }
): Omit<BPIData, 'id' | 'lastUpdated' | 'bpiScore'> => {
  const factors: BPIFactors = {
    // Use provided performance data
    revenuePerformance: performanceData.revenuePerformance || 75,
    clientRetention: performanceData.retentionRate || 80,
    averageTicket: performanceData.avgTicket || 70,
    clientSatisfaction: performanceData.clientSatisfaction || 85,

    // Set defaults for other factors
    productSalesRate: 60,
    serviceSpeed: 75,
    rebookRate: 70,
    noShowRate: 15, // Lower is better
    punctuality: 90,
    uniformCompliance: 95,
    workstationCleanliness: 90,
    skillDevelopment: 70,
    teamwork: 80,
    initiative: 75,
    newClientAcquisition: 65,
    clientCommunication: 85,
    socialMediaEngagement: 60
  };

  return {
    barberId,
    barberName,
    factors,
    manualAdjustment: 0,
    adjustmentReason: '',
    notes: 'Auto-generated from performance data',
    updatedBy: 'system'
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// BPI rework (Jose 2026-09-27) — the NEW per-barber record. Stores only the MANUAL param values (the
// owner-slid ones) + the transparent ± adjustment + reason + notes. Computed params are never stored —
// they resolve live from real data (bpiMetricsService). Coexists with the legacy shape above (read-only
// migration: legacy `factors` seed a manual value only if the new record has none).
// ─────────────────────────────────────────────────────────────────────────────
export interface BpiRecord {
  barberId: string;
  manualValues: Record<string, number>;  // 0-100 per MANUAL param key
  adjustment: number;                     // the transparent ± nudge
  adjustmentReason: string;               // required whenever adjustment !== 0 (enforced in UI)
  notes: string;                          // free notes → fed to the AI (goals/growth)
  finalScore: number;                     // last computed 0-100 (denormalized for cards/reports)
  updatedBy: string;
  updatedAt: Date;
}

export const loadBpiRecord = async (barberId: string): Promise<BpiRecord | null> => {
  try {
    const snap = await getDoc(doc(db, 'bpiData', barberId));
    if (!snap.exists()) return null;
    const d: any = snap.data();
    return {
      barberId,
      manualValues: d.manualValues || (d.factors ? { ...d.factors } : {}), // legacy factors seed manuals
      adjustment: typeof d.adjustment === 'number' ? d.adjustment : (typeof d.manualAdjustment === 'number' ? d.manualAdjustment : 0),
      adjustmentReason: d.adjustmentReason || '',
      notes: d.notes || '',
      finalScore: typeof d.finalScore === 'number' ? d.finalScore : (typeof d.bpiScore === 'number' ? d.bpiScore : 0),
      updatedBy: d.updatedBy || 'system',
      updatedAt: d.updatedAt?.toDate?.() || d.lastUpdated?.toDate?.() || new Date(),
    };
  } catch (e) {
    console.error('[bpi] loadBpiRecord failed:', e);
    return null;
  }
};

export const saveBpiRecord = async (
  rec: Pick<BpiRecord, 'barberId' | 'manualValues' | 'adjustment' | 'adjustmentReason' | 'notes' | 'finalScore'>,
  updatedBy: string,
): Promise<void> => {
  await setDoc(doc(db, 'bpiData', rec.barberId), {
    manualValues: rec.manualValues,
    adjustment: rec.adjustment,
    adjustmentReason: rec.adjustmentReason,
    notes: rec.notes,
    finalScore: rec.finalScore,
    updatedBy,
    updatedAt: Timestamp.now(),
  }, { merge: true });
};
